import React from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { Theme } from "@radix-ui/themes"
import { Toaster } from "react-hot-toast"
import { AuthProvider } from "./contexts/AuthContext"
import { RestaurantAuthProvider } from "./contexts/RestaurantAuthContext"
import { ThemeProvider, useTheme } from "./contexts/ThemeContext"
import { NotificationProvider } from "./contexts/NotificationContext"
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext"
import { NewOrderProvider } from "./contexts/NewOrderContext"
import { SocketProvider } from "./contexts/SocketContext"
import { useNotificationService } from "./services/notificationService"
import ProtectedRoute from "./components/ProtectedRoute"
import RestaurantProtectedRoute from "./components/RestaurantProtectedRoute"
import Layout from "./components/Layout"
import RestaurantLayout from "./components/RestaurantLayout"
import LandingPage from "./pages/LandingPage"
import ContactPage from "./pages/ContactPage"
import LoginPage from "./pages/auth/LoginPage"
import DashboardPage from "./pages/DashboardPage"
import OrdersPage from "./pages/OrdersPage"
import RestaurantsPage from "./pages/RestaurantsPage"
import CaptainsPage from "./pages/CaptainsPage"
import UsersPage from "./pages/UsersPage"
import NotificationsPage from "./pages/NotificationsPage"
import AnalyticsPage from "./pages/AnalyticsPage"
import SettingsPage from "./pages/SettingsPage"
import PromotionsPage from "./pages/PromotionsPage"
import FeedbackPage from "./pages/FeedbackPage"
import FoodItemsPage from "./pages/FoodItemsPage"
import ProfilePage from "./pages/ProfilePage"
import RestaurantDashboardPage from "./pages/restaurant/RestaurantDashboardPage"
import RestaurantFoodItemsPage from "./pages/restaurant/RestaurantFoodItemsPage"
import RestaurantTablesPage from "./pages/restaurant/RestaurantTablesPage"
import RestaurantOrdersPage from "./pages/restaurant/RestaurantOrdersPage"
import KitchenOrdersPage from "./pages/restaurant/KitchenOrdersPage"
import WaiterOrdersPage from "./pages/restaurant/WaiterOrdersPage"
import RestaurantLoginPage from "./pages/restaurant/RestaurantLoginPage"
import RestaurantStaffLoginPage from "./pages/restaurant/RestaurantStaffLoginPage"
import RestaurantRegisterPage from "./pages/restaurant/RestaurantRegisterPage"
import TableOrderPage from "./pages/TableOrderPage"
import TableLandingPage from "./pages/TableLandingPage"
import TableOrderStatusPage from "./pages/TableOrderStatusPage"
import MenuBookPage from "./pages/MenuBookPage"
import MenuBookRestaurantPage from "./pages/MenuBookRestaurantPage"
import RestaurantAnalyticsPage from "./pages/restaurant/RestaurantAnalyticsPage"
import RestaurantFoodItemsPerformancePage from "./pages/restaurant/RestaurantFoodItemsPerformancePage"
import RestaurantReportsPage from "./pages/restaurant/RestaurantReportsPage"
import DailySalesReport from "./components/ui/reports/DailySalesReport"
import VoidsCompsReport from "./components/ui/reports/VoidsCompsReport"
import MenuPopularityReport from "./components/ui/reports/MenuPopularityReport"
import POSDashboardPage from "./pages/restaurant/POSDashboardPage"
import POSNewOrderPage from "./pages/restaurant/POSNewOrderPage"
import DeliveryOrdersPage from "./pages/restaurant/DeliveryOrdersPage"
import RestaurantUsersPage from "./pages/restaurant/RestaurantUsersPage"
import RouteGuard from "./components/RouteGuard"
import FinancialsPage from "./pages/FinancialsPage"
import SubscriptionsPage from "./pages/SubscriptionsPage"
import BannersPage from "./pages/BannersPage"
import PushNotificationsPage from "./pages/PushNotificationsPage"
import UsersRolesPage from "./pages/UsersRolesPage"
import EmailTemplatesPage from "./pages/EmailTemplatesPage"
import RestaurantSettingsPage from "./pages/restaurant/RestaurantSettingsPage"
import ProfileRoute from "./components/ProfileRoute"
import CustomersPage from "./pages/CustomersPage"
import AdminDeliveryOrdersPage from "./pages/AdminDeliveryOrdersPage"
import { SessionExpiryDialog } from "./components/SessionExpiryDialog"

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Component to initialize notification service at app level
const NotificationInitializer: React.FC = () => {
  useNotificationService()
  return null
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <ThemeProvider>
          <RadixThemeWrapper>
            <AuthProvider>
              <RestaurantAuthProvider>
                {/* SocketProvider must be inside RestaurantAuth to access token */}
                <SocketProvider>
                  <NotificationProvider>
                    <NewOrderProvider>
                      <NotificationInitializer />
                      <Router>
                        <div className="min-h-screen">
                          <SessionExpiryDialog />
                          <Routes>
                            <Route path="/login" element={<LoginPage />} />

                            {/* Restaurant Authentication Routes */}
                            <Route path="/restaurant/login" element={<RestaurantLoginPage />} />
                            <Route path="/restaurant/staff/login" element={<RestaurantStaffLoginPage />} />
                            <Route path="/restaurant/register" element={<RestaurantRegisterPage />} />

                            {/* Table Order Routes - Public access for customers */}
                            <Route path="/table/:qrCode" element={<TableLandingPage />} />
                            <Route path="/table/:qrCode/menu" element={<TableOrderPage />} />
                            <Route path="/order-status/:confirmationCode" element={<TableOrderStatusPage />} />
                            <Route path="/order-status" element={<TableOrderStatusPage />} />

                            {/* Menu Book Routes - Public access */}
                            <Route path="/menu" element={<MenuBookPage />} />
                            <Route path="/menu/:slug" element={<MenuBookRestaurantPage />} />
                            <Route
                              path="/"
                              element={<LandingPage />}
                            />
                            <Route
                              path="/contact"
                              element={<ContactPage />}
                            />
                            <Route
                              path="/admin"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <Navigate to="/admin/dashboard" replace />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/dashboard"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <DashboardPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/orders"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <AdminDeliveryOrdersPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/restaurants"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <RestaurantsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/food-items"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <FoodItemsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/captains"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <CaptainsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/users"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <UsersPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/notifications"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <NotificationsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/analytics"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <AnalyticsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/promotions"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <PromotionsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/feedback"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <FeedbackPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/settings"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <SettingsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/financials"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <FinancialsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/subscriptions"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <SubscriptionsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/banners"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <BannersPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/push-notifications"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <PushNotificationsPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/users-roles"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <UsersRolesPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/customers"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <CustomersPage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/profile"
                              element={
                                <ProtectedRoute>
                                  <Layout>
                                    <ProfilePage />
                                  </Layout>
                                </ProtectedRoute>
                              }
                            />

                            {/* Restaurant Portal Routes */}
                            <Route
                              path="/restaurant/:restaurantId/dashboard"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="dashboard">
                                      <RestaurantDashboardPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/food-items"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="food-items">
                                      <RestaurantFoodItemsPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/tables"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="tables">
                                      <RestaurantTablesPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/orders"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="orders">
                                      <RestaurantOrdersPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/analytics"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="analytics">
                                      <RestaurantAnalyticsPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/food-items-performance"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="food-items-performance">
                                      <RestaurantFoodItemsPerformancePage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/reports"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="reports">
                                      <RestaurantReportsPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/reports/daily-sales"
                              element={
                                <RestaurantProtectedRoute>
                                  <DailySalesReport />
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/reports/voids-comps"
                              element={
                                <RestaurantProtectedRoute>
                                  <VoidsCompsReport />
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/reports/menu-popularity"
                              element={
                                <RestaurantProtectedRoute>
                                  <MenuPopularityReport />
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/pos"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <POSDashboardPage />
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/pos/new-order"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <POSNewOrderPage />
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/delivery-orders"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <DeliveryOrdersPage />
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/users"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="users">
                                      <RestaurantUsersPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/settings"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RestaurantSettingsPage />
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/kitchen-orders"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="kitchen-orders">
                                      <KitchenOrdersPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/waiter-orders"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <RouteGuard routeName="waiter-orders">
                                      <WaiterOrdersPage />
                                    </RouteGuard>
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                            <Route
                              path="/restaurant/:restaurantId/profile"
                              element={
                                <RestaurantProtectedRoute>
                                  <RestaurantLayout>
                                    <ProfileRoute />
                                  </RestaurantLayout>
                                </RestaurantProtectedRoute>
                              }
                            />
                          </Routes>
                        </div>
                      </Router>
                    </NewOrderProvider>
                  </NotificationProvider>
                </SocketProvider>
              </RestaurantAuthProvider>
            </AuthProvider>
          </RadixThemeWrapper>
        </ThemeProvider>
      </LanguageProvider>
    </QueryClientProvider>
  )
}

// Wrapper component to integrate Radix Theme with our custom theme context
const RadixThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDarkMode } = useTheme()
  const { isRTL } = useLanguage()

  return (
    <Theme
      accentColor="red"
      grayColor="slate"
      radius="medium"
      scaling="100%"
      appearance={isDarkMode ? "dark" : "light"}
      style={{ direction: isRTL ? "rtl" : "ltr" }}
    >
      {children}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: isDarkMode ? '#1a1a1a' : '#ffffff',
            color: isDarkMode ? '#ffffff' : '#000000',
            border: `1px solid ${isDarkMode ? '#333' : '#e5e5e5'}`,
            borderRadius: '8px',
            fontSize: '14px',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#ffffff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#ffffff',
            },
          },
        }}
      />
    </Theme>
  )
}

export default App
